import React from 'react';
import { Phone, Ruler, Scale, AlertTriangle, FileText, Gauge } from 'lucide-react';
export function VehicleLoadSizePage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-4009">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-4010">
        <div className="container mx-auto px-4" data-id="element-4011">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-4012">
            Michigan Weight & Axle Load Limits
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-4013">
            Maximum Legal Truck Loadings and Dimensions
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-50 border-b-2 border-yellow-500" data-id="element-4014">
        <div className="container mx-auto px-4 py-4" data-id="element-4015">
          <div className="max-w-4xl mx-auto" data-id="element-4016">
            <div className="flex items-start" data-id="element-4017">
              <AlertTriangle className="text-yellow-600 mr-3 flex-shrink-0 mt-1" size={24} data-id="element-4018" />
              <div data-id="element-4019">
                <p className="text-sm text-gray-800 mb-2" data-id="element-4020">
                  <strong data-id="element-4021">Important:</strong> Due to frequent changes in federal
                  and state regulations, the Michigan Center for Truck Safety
                  cannot ensure the accuracy of the material contained beyond
                  the date of publication.
                </p>
                <p className="text-sm text-gray-800" data-id="element-4022">
                  For current information, contact the Center at{' '}
                  <a href="tel:8006824682" className="text-blue-600 hover:underline font-semibold" data-id="element-4023">
                    (800) 682-4682
                  </a>
                  . This document is not intended for legal purposes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-4024">
        <div className="max-w-6xl mx-auto" data-id="element-4025">
          {/* Seasonal Restrictions Note */}
          <div className="bg-red-50 border-2 border-red-500 rounded-lg shadow-md p-6 mb-8" data-id="element-4026">
            <h2 className="text-xl font-bold text-red-900 mb-4" data-id="element-4027">
              SEASONAL LOAD RESTRICTIONS
            </h2>
            <div className="space-y-3 text-gray-800" data-id="element-4028">
              <p className="font-semibold" data-id="element-4029">
                When restricted loadings are in effect:
              </p>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-4030">
                <div className="bg-white rounded-lg p-4" data-id="element-4031">
                  <p className="font-bold text-red-900 mb-2" data-id="element-4032">
                    Rigid Pavements:
                  </p>
                  <ul className="space-y-1 text-sm" data-id="element-4033">
                    <li data-id="element-4034">
                      • Normal maximum axle weights reduced <strong data-id="element-4035">25%</strong>
                    </li>
                    <li data-id="element-4036">
                      • Maximum wheel load: <strong data-id="element-4037">525 lbs per inch</strong> of
                      tire width
                    </li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4" data-id="element-4038">
                  <p className="font-bold text-red-900 mb-2" data-id="element-4039">
                    Flexible Pavements:
                  </p>
                  <ul className="space-y-1 text-sm" data-id="element-4040">
                    <li data-id="element-4041">
                      • Normal maximum axle weights reduced <strong data-id="element-4042">35%</strong>
                    </li>
                    <li data-id="element-4043">
                      • Maximum wheel load: <strong data-id="element-4044">450 lbs per inch</strong> of
                      tire width
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Maximum Overall Dimensions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4045">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-4046">
              <Ruler className="mr-3 text-[#B1050B]" size={28} data-id="element-4047" />
              Maximum Overall Dimensions
            </h2>

            <div className="space-y-4" data-id="element-4048">
              <div className="grid md:grid-cols-2 gap-4" data-id="element-4049">
                <div className="bg-blue-50 rounded-lg p-4" data-id="element-4050">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-4051">Width</p>
                  <p className="text-2xl font-bold text-blue-600" data-id="element-4052">96 inches</p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-4053">
                    102 inches on designated highways
                  </p>
                </div>

                <div className="bg-green-50 rounded-lg p-4" data-id="element-4054">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-4055">Height</p>
                  <p className="text-2xl font-bold text-green-600" data-id="element-4056">
                    13 feet, 6 inches
                  </p>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4" data-id="element-4057">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-4058">
                    Semitrailer Length (with load)
                  </p>
                  <p className="text-2xl font-bold text-yellow-600" data-id="element-4059">53 feet</p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-4060">
                    50 feet on non-designated highways
                  </p>
                </div>

                <div className="bg-purple-50 rounded-lg p-4" data-id="element-4061">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-4062">
                    Other Vehicle Length
                  </p>
                  <p className="text-2xl font-bold text-purple-600" data-id="element-4063">40 feet</p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-4064">
                    Excluding impact absorbing bumpers
                  </p>
                </div>

                <div className="bg-red-50 rounded-lg p-4" data-id="element-4065">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-4066">
                    Combination Length
                  </p>
                  <p className="text-2xl font-bold text-red-600" data-id="element-4067">65 feet</p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-4068">
                    Truck-tractor + semitrailer + trailer
                  </p>
                </div>

                <div className="bg-indigo-50 rounded-lg p-4" data-id="element-4069">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-4070">
                    Axle Limitation
                  </p>
                  <p className="text-2xl font-bold text-indigo-600" data-id="element-4071">
                    11 axles max
                  </p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-4072">
                    Combination of vehicles
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 mt-6" data-id="element-4073">
                <h3 className="font-bold text-gray-900 mb-4" data-id="element-4074">
                  Additional Requirements for Semitrailers Longer than 50 feet:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-4075">
                  <li className="flex items-start" data-id="element-4076">
                    <span className="mr-2" data-id="element-4077">•</span>
                    <span data-id="element-4078">
                      Wheelbase of <strong data-id="element-4079">37 to 41 feet</strong> (kingpin to
                      center of axles/tandem assembly)
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-4080">
                    <span className="mr-2" data-id="element-4081">•</span>
                    <span data-id="element-4082">
                      Limited to <strong data-id="element-4083">3 axles</strong>
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-4084">
                    <span className="mr-2" data-id="element-4085">•</span>
                    <span data-id="element-4086">
                      Operate on <strong data-id="element-4087">designated highways only</strong>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-lg p-6" data-id="element-4088">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-4089">
                  Projections & Overhang:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-4090">
                  <li data-id="element-4091">
                    • <strong data-id="element-4092">Front projection:</strong> 3 feet maximum
                  </li>
                  <li data-id="element-4093">
                    • <strong data-id="element-4094">Rear overhang:</strong> Any amount if legal length
                    not exceeded
                  </li>
                  <li data-id="element-4095">
                    • If rear overhang is <strong data-id="element-4096">4 feet or more:</strong>{' '}
                    12-inch red square flag (daytime) and red light/lantern
                    (night)
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Weight Limits Table */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4097">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-4098">
              <Scale className="mr-3 text-[#B1050B]" size={28} data-id="element-4099" />
              Maximum Allowable Axle Loadings
            </h2>

            <div className="overflow-x-auto" data-id="element-4100">
              <table className="w-full border-collapse text-sm" data-id="element-4101">
                <thead data-id="element-4102">
                  <tr className="bg-gray-200" data-id="element-4103">
                    <th className="border border-gray-300 px-3 py-2 text-left" data-id="element-4104">
                      Spacing Between Axles
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-center" data-id="element-4105">
                      Normal Loadings
                      <br data-id="element-4106" />
                      (55 MPH)
                      <br data-id="element-4107" />
                      Vehicles &gt;80,000 lbs
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-center" data-id="element-4108">
                      Normal Loadings
                      <br data-id="element-4109" />
                      (55 MPH)
                      <br data-id="element-4110" />
                      Vehicles ≤80,000 lbs
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-center" data-id="element-4111">
                      Seasonal (35 MPH)
                      <br data-id="element-4112" />
                      Rigid
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-center" data-id="element-4113">
                      Seasonal (35 MPH)
                      <br data-id="element-4114" />
                      Flexible
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-4115">
                  <tr className="bg-white" data-id="element-4116">
                    <td className="border border-gray-300 px-3 py-2 font-semibold" data-id="element-4117">
                      9 feet or over
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4118">
                      18,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4119">
                      20,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4120">
                      13,500 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4121">
                      11,700 lbs
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-4122">
                    <td className="border border-gray-300 px-3 py-2 font-semibold" data-id="element-4123">
                      More than 3½ ft but less than 9 ft
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4124">
                      13,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4125">
                      13,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4126">
                      9,750 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4127">
                      8,450 lbs
                    </td>
                  </tr>
                  <tr className="bg-white" data-id="element-4128">
                    <td className="border border-gray-300 px-3 py-2 font-semibold" data-id="element-4129">
                      Tandem axle assembly
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4130">
                      *16,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4131">
                      34,000 lbs on tandem
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4132">
                      **12,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4133">
                      ***10,400 lbs
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-4134">
                    <td className="border border-gray-300 px-3 py-2 font-semibold" data-id="element-4135">
                      Less than 3½ feet
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4136">
                      9,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4137">
                      9,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4138">
                      6,750 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4139">
                      5,850 lbs
                    </td>
                  </tr>
                  <tr className="bg-blue-50" data-id="element-4140">
                    <td className="border border-gray-300 px-3 py-2 font-semibold" data-id="element-4141">
                      Max wheel load (lbs/inch tire width)
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4142">
                      700 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4143">
                      700 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4144">
                      525 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-4145">
                      450 lbs
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 space-y-3 text-sm text-gray-700" data-id="element-4146">
              <p data-id="element-4147">
                <strong data-id="element-4148">*</strong> On any legal combination exceeding 80,000
                lbs, only ONE tandem axle assembly permitted at 16,000 lbs per
                axle; others limited to 13,000 lbs per axle. When ≤80,000 lbs,
                TWO tandem assemblies permitted at 16,000 lbs per axle.
              </p>
              <p data-id="element-4149">
                <strong data-id="element-4150">**</strong> On any legal combination exceeding 80,000
                lbs, only ONE tandem axle assembly permitted at 12,000 lbs per
                axle; others limited to 9,750 lbs per axle. When ≤80,000 lbs,
                TWO tandem assemblies permitted at 12,000 lbs per axle.
              </p>
              <p data-id="element-4151">
                <strong data-id="element-4152">***</strong> On any legal combination exceeding 80,000
                lbs, only ONE tandem axle assembly permitted at 10,400 lbs per
                axle; others limited to 8,450 lbs per axle. When ≤80,000 lbs,
                TWO tandem assemblies permitted at 10,400 lbs per axle.
              </p>
            </div>
          </div>

          {/* Gross Vehicle Weight Formula */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-4153">
            <h2 className="text-3xl font-bold mb-6" data-id="element-4154">
              Gross Vehicle Weight Limit
            </h2>

            <div className="bg-white/10 rounded-lg p-6 mb-6" data-id="element-4155">
              <p className="text-xl font-bold mb-4" data-id="element-4156">Maximum: 80,000 lbs</p>
              <p className="mb-4" data-id="element-4157">
                Plus the following formula for consecutive axle groups:
              </p>

              <div className="bg-white/20 rounded-lg p-4 font-mono text-lg" data-id="element-4158">
                <p className="text-center" data-id="element-4159">W = 500 × (LN/(N-1) + 12N + 36)</p>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6" data-id="element-4160">
              <p className="font-bold mb-3" data-id="element-4161">Where:</p>
              <ul className="space-y-2" data-id="element-4162">
                <li data-id="element-4163">
                  <strong data-id="element-4164">W</strong> = Overall gross weight on group of 2+
                  consecutive axles (nearest 500 lbs)
                </li>
                <li data-id="element-4165">
                  <strong data-id="element-4166">L</strong> = Distance in feet between extremes of axle
                  group
                </li>
                <li data-id="element-4167">
                  <strong data-id="element-4168">N</strong> = Number of axles in the group
                </li>
              </ul>
            </div>

            <div className="bg-yellow-50 text-gray-900 rounded-lg p-6 mt-6" data-id="element-4169">
              <p className="font-bold mb-2" data-id="element-4170">Exception:</p>
              <p className="text-sm" data-id="element-4171">
                2 consecutive sets of tandem axles may carry{' '}
                <strong data-id="element-4172">34,000 lbs each</strong> if first and last axles are{' '}
                <strong data-id="element-4173">not less than 36 feet apart</strong>, and gross vehicle
                weight does not exceed 80,000 lbs including all enforcement
                tolerances.
              </p>
            </div>
          </div>

          {/* Exceptions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4174">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-4175">
              Exceptions
            </h2>

            <div className="space-y-6" data-id="element-4176">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-4177">
                <h3 className="font-bold text-blue-900 mb-3" data-id="element-4178">
                  WIDTH EXCEPTIONS:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-4179">
                  <li data-id="element-4180">
                    •{' '}
                    <strong data-id="element-4181">
                      Unprocessed Logs, Pulpwood, Wood Bolts, Agricultural
                      Products, Concrete Pipe:
                    </strong>{' '}
                    108 inches (load only)
                  </li>
                  <li data-id="element-4182">
                    • <strong data-id="element-4183">Buses:</strong> 102 inches
                  </li>
                  <li data-id="element-4184">
                    • <strong data-id="element-4185">Implements of Husbandry:</strong> Any width for
                    normal farming operations
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-4186">
                <h3 className="font-bold text-green-900 mb-3" data-id="element-4187">
                  LENGTH EXCEPTIONS:
                </h3>

                <div className="space-y-4" data-id="element-4188">
                  <div data-id="element-4189">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-4190">
                      Truck-Tractor + Semitrailer + Trailer (or 2 Semitrailers):
                    </p>
                    <p className="text-gray-700" data-id="element-4191">
                      No overall length limit if each unit ≤28 ft 6 in OR
                      combined length ≤58 ft (measured front of first to rear of
                      second unit)
                    </p>
                  </div>

                  <div data-id="element-4192">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-4193">
                      Truck + Trailer/Semitrailer:
                    </p>
                    <p className="text-gray-700" data-id="element-4194">
                      Overall length ≤65 feet (designated highways only)
                    </p>
                  </div>

                  <div data-id="element-4195">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-4196">
                      Logging Vehicles (saw logs, pulpwood, tree length poles):
                    </p>
                    <p className="text-gray-700" data-id="element-4197">
                      Overall length ≤70 feet including load (designated
                      highways only)
                    </p>
                  </div>

                  <div data-id="element-4198">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-4199">
                      Auto/RV/Boat Transporters:
                    </p>
                    <p className="text-gray-700" data-id="element-4200">
                      ≤65 feet (stinger-steered ≤75 feet). Load may extend 3 ft
                      front, 4 ft rear
                    </p>
                  </div>

                  <div data-id="element-4201">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-4202">
                      Articulated Buses (Public Transit):
                    </p>
                    <p className="text-gray-700" data-id="element-4203">Maximum 65 feet</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Special Vehicles */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4204">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-4205">
              Special Vehicle Requirements
            </h2>

            <div className="space-y-4" data-id="element-4206">
              <div className="bg-yellow-50 rounded-lg p-6" data-id="element-4207">
                <h3 className="font-bold text-yellow-900 mb-3" data-id="element-4208">
                  MOBILE HOMES:
                </h3>
                <ul className="space-y-1 text-gray-700" data-id="element-4209">
                  <li data-id="element-4210">
                    • Body length: <strong data-id="element-4211">45 feet</strong>
                  </li>
                  <li data-id="element-4212">
                    • With towing unit: <strong data-id="element-4213">60 feet</strong>
                  </li>
                  <li data-id="element-4214">
                    • Width: <strong data-id="element-4215">8 feet 4 inches</strong>
                  </li>
                  <li data-id="element-4216">
                    • Height: <strong data-id="element-4217">12 feet 6 inches</strong>
                  </li>
                </ul>
              </div>

              <div className="bg-red-50 rounded-lg p-6" data-id="element-4218">
                <h3 className="font-bold text-red-900 mb-3" data-id="element-4219">
                  FLAMMABLE LIQUID TRANSPORT (Flash point ≤70°F):
                </h3>
                <ul className="space-y-1 text-gray-700" data-id="element-4220">
                  <li data-id="element-4221">
                    • Must meet Department of State Police safety standards
                  </li>
                  <li data-id="element-4222">
                    • Vehicles manufactured after July 27, 1978:{' '}
                    <strong data-id="element-4223">11 feet 8½ inches maximum height</strong>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Movement Restrictions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4224">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-4225">
              <Gauge className="mr-3 text-[#B1050B]" size={28} data-id="element-4226" />
              Movement Restrictions
            </h2>

            <div className="space-y-4" data-id="element-4227">
              <div className="bg-red-50 border-l-4 border-red-500 p-6" data-id="element-4228">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-4229">
                  Counties with 600,000+ Population:
                </h3>

                <div className="space-y-3" data-id="element-4230">
                  <div data-id="element-4231">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-4232">
                      Truck/Truck-Tractor + Semitrailer (more than 9,000
                      gallons):
                    </p>
                    <p className="text-gray-700" data-id="element-4233">
                      Movement allowed ONLY between{' '}
                      <strong data-id="element-4234">midnight to 6:00 a.m.</strong> on designated
                      routes
                    </p>
                  </div>

                  <div data-id="element-4235">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-4236">
                      Truck-Tractor + Semitrailer + Trailer (or 2 Semitrailers):
                    </p>
                    <p className="text-gray-700" data-id="element-4237">
                      Movement allowed ONLY between{' '}
                      <strong data-id="element-4238">midnight to 6:00 a.m.</strong> on designated
                      routes
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Maximum Truck Speed */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4239">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-4240">
              Maximum Truck Speed
            </h2>

            <div className="bg-blue-50 rounded-lg p-6" data-id="element-4241">
              <p className="text-gray-700 mb-4" data-id="element-4242">
                No truck, tractor or tractor with trailer, or any combination
                with gross weight (loaded or unloaded){' '}
                <strong data-id="element-4243">exceeding 10,000 lbs</strong> shall exceed:
              </p>
              <ul className="space-y-2 text-gray-700" data-id="element-4244">
                <li data-id="element-4245">
                  • The posted speed limit OR the speed limit on permit
                  (whichever is less)
                </li>
                <li data-id="element-4246">
                  • <strong data-id="element-4247">35 MPH</strong> where reduced loadings are enforced
                  during seasonal weight restrictions
                </li>
              </ul>
            </div>
          </div>

          {/* Special Permits */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-4248">
            <h2 className="text-3xl font-bold mb-6" data-id="element-4249">
              Special Permit Information
            </h2>

            <div className="space-y-4" data-id="element-4250">
              <p className="text-lg" data-id="element-4251">
                Vehicles or loads exceeding legal dimensions or weights require
                a <strong data-id="element-4252">SPECIAL TRANSPORT PERMIT</strong> for travel.
              </p>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-4253">
                <h3 className="font-bold mb-3" data-id="element-4254">Permits issued only for:</h3>
                <p className="text-gray-100" data-id="element-4255">
                  Occasional movement of oversize or overweight vehicles/loads
                  that cannot be dismantled, reduced, or rearranged to come
                  within legal limits.
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-4256">
                <h3 className="font-bold mb-3" data-id="element-4257">Application Methods:</h3>
                <ul className="space-y-2 text-gray-100" data-id="element-4258">
                  <li data-id="element-4259">
                    • Michigan Permit and Routing System (MiPARs) computer
                    program
                  </li>
                  <li data-id="element-4260">
                    • In person at MDOT Transportation Service Centers (TSCs)
                  </li>
                  <li data-id="element-4261">• Through a permit service</li>
                </ul>
              </div>

              <div className="bg-red-50 text-gray-900 rounded-lg p-6" data-id="element-4262">
                <p className="font-bold" data-id="element-4263">
                  ⚠️ No overweight permits issued when weight restrictions are
                  in effect
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-4264">
            <h2 className="text-3xl font-bold mb-4" data-id="element-4265">
              Need Help with Weight & Load Compliance?
            </h2>
            <p className="text-xl mb-6" data-id="element-4266">
              Expert guidance on Michigan weight limits, dimensions, and special
              permits
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-4267">
              <a href="tel:2484788382" className="inline-flex items-center justify-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-4268">
                <Phone className="mr-3" size={24} data-id="element-4269" />
                Call (248) 478-8382
              </a>
              <a href="tel:8006824682" className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-4270">
                <Phone className="mr-3" size={24} data-id="element-4271" />
                MI Truck Safety: (800) 682-4682
              </a>
            </div>
            <p className="mt-4 text-gray-300" data-id="element-4272">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}